import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, ShoppingBag, Loader2, Send, Plus, Minus, Trash2, Shield, AlertTriangle, LogOut } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { punchout } from '../../lib/govApi';
import { toast } from '../../hooks/use-toast';

/**
 * Punchout-mode catalog: the user arrives here from their procurement system's
 * browser redirect (with ?token=...) and never logs into the gov portal at all.
 *
 * Flow:
 *   1. /api/punchout/start/{token} → 302 redirect → /gov/punchout/catalog?token=...
 *   2. We load the session via /api/punchout/sessions/{token}
 *   3. User browses & adds items to a local cart
 *   4. On "Transfer Cart" → POST /sessions/{token}/transfer → receive cxml_base64 +
 *      browser_form_post_url, then auto-submit a real <form> to that URL.
 */
const GovPunchoutCatalog = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [session, setSession] = useState(null);
  const [sessionError, setSessionError] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [transferring, setTransferring] = useState(false);
  const formRef = useRef(null);
  const [formAction, setFormAction] = useState('');
  const [formEncoding, setFormEncoding] = useState('cxml-base64');
  const [formCxmlValue, setFormCxmlValue] = useState('');

  const fetchCatalog = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        punchout.getCatalog(token, {
          ...(category !== 'all' ? { category } : {}),
          ...(q.length >= 2 ? { q } : {}),
        }),
        punchout.getCategories(token),
      ]);
      setProducts(pRes.data || []);
      setCategories(cRes.data || []);
    } catch (e) {
      toast({ title: 'Catalog error', description: e.response?.data?.detail || 'Failed to load catalog', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, category, q]);

  useEffect(() => {
    if (!token) {
      setSessionError('Missing punchout token in URL.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await punchout.getSession(token);
        setSession(res.data);
      } catch (e) {
        setSessionError(e.response?.data?.detail || 'Punchout session is not valid');
      }
    })();
  }, [token]);

  useEffect(() => {
    if (session) fetchCatalog();
  }, [session, category, fetchCatalog]);

  const onSearch = (e) => { e.preventDefault(); fetchCatalog(); };

  const addToCart = (p) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === p.id);
      if (existing) return prev.map(i => i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, {
        product_id: p.id,
        sku: p.sku,
        name: p.name,
        price: Number(p.contract_price || p.price),
        image: p.image,
        manufacturer: p.manufacturer,
        uom: p.uom,
        quantity: 1,
      }];
    });
    toast({ title: 'Added', description: p.name });
  };

  const updateQty = (productId, qty) => {
    if (qty <= 0) {
      setCart(prev => prev.filter(i => i.product_id !== productId));
    } else {
      setCart(prev => prev.map(i => i.product_id === productId ? { ...i, quantity: qty } : i));
    }
  };

  const total = useMemo(
    () => cart.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 0), 0),
    [cart]
  );
  const count = cart.reduce((s, i) => s + i.quantity, 0);

  const transferCart = async () => {
    if (!cart.length) return;
    setTransferring(true);
    try {
      const res = await punchout.transfer(token, cart.map(i => ({
        product_id: i.product_id,
        sku: i.sku,
        name: i.name,
        price: i.price,
        quantity: i.quantity,
      })));
      const { browser_form_post_url, cxml_base64, cxml_urlencoded, encoding, oci_html } = res.data || {};
      // OCI path: server returned a complete HTML page; write it into a hidden iframe so it auto-submits
      if (res.data?.protocol === 'oci' && oci_html) {
        const w = window.open('', '_top');
        if (w) { w.document.open(); w.document.write(oci_html); w.document.close(); }
        return;
      }
      if (!browser_form_post_url || (!cxml_base64 && !cxml_urlencoded)) {
        toast({ title: 'Transfer failed', description: 'Missing return URL from server', variant: 'destructive' });
        return;
      }
      setFormAction(browser_form_post_url);
      setFormEncoding(encoding || 'cxml-base64');
      setFormCxmlValue(encoding === 'cxml-urlencoded' ? cxml_urlencoded : cxml_base64);
      // Wait one tick so React renders the form, then submit it.
      setTimeout(() => { try { formRef.current?.submit(); } catch (_) { /* ignore */ } }, 50);
    } catch (e) {
      toast({ title: 'Transfer failed', description: e.response?.data?.detail || 'Could not transfer cart', variant: 'destructive' });
    } finally {
      setTransferring(false);
    }
  };

  if (sessionError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md border-2 border-red-200" data-testid="punchout-session-error">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-800">Punchout session error</h1>
            <p className="text-sm text-slate-500 mt-2">{sessionError}</p>
            <p className="text-xs text-slate-400 mt-4">Please return to your procurement system and start a new punchout session.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="punchout-catalog-page">
      {/* Punchout banner */}
      <div className="bg-amber-50 border-b-2 border-amber-300 text-amber-900 text-xs py-2 px-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-amber-700" />
          <span className="font-medium">
            Punchout session active for <strong>{session.partner_name}</strong>
            {session.user_email && <> · {session.user_email}</>}
          </span>
        </div>
        <span className="text-amber-700">Expires {session.expires_at ? new Date(session.expires_at).toLocaleTimeString() : 'soon'}</span>
      </div>

      {/* Header */}
      <header className="bg-blue-800 text-white">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded bg-white text-blue-800 flex items-center justify-center font-bold">DES</div>
            <div className="leading-tight">
              <div className="font-bold text-base sm:text-lg">USKE Punchout Catalog</div>
              <div className="text-xs text-blue-200">Washington State Contract 09925</div>
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="font-semibold">{count} item{count === 1 ? '' : 's'}</div>
            <div className="text-blue-200 text-xs">${total.toFixed(2)} cart total</div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Catalog */}
        <section className="lg:col-span-3 space-y-4">
          <form onSubmit={onSearch} className="flex flex-wrap gap-2 items-end">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input className="pl-10" placeholder="Search products or SKU" value={q} onChange={(e) => setQ(e.target.value)} data-testid="punchout-search-input" />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-48" data-testid="punchout-category-select"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.slug.replace(/-/g, ' ')} ({c.count})</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white">Search</Button>
          </form>

          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-10 w-10 animate-spin text-blue-700" /></div>
          ) : products.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="p-10 text-center text-slate-500">No products match your search.</CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {products.map(p => (
                <Card key={p.id} className="border-2 hover:shadow-lg transition" data-testid={`punchout-card-${p.id}`}>
                  <CardContent className="p-3">
                    <div className="aspect-square bg-slate-100 rounded mb-2 overflow-hidden">
                      {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
                    </div>
                    <h3 className="font-semibold text-slate-800 text-sm line-clamp-2 min-h-[2.5rem]">{p.name}</h3>
                    <div className="text-xs text-slate-500 mt-1">SKU {p.sku}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <div className="text-base font-bold text-blue-700">${Number(p.contract_price || p.price).toFixed(2)}</div>
                        <div className="text-[10px] uppercase tracking-wide text-emerald-700">Contract</div>
                      </div>
                      <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" onClick={() => addToCart(p)} data-testid={`punchout-add-${p.id}`}>
                        <ShoppingBag className="h-4 w-4 mr-1" /> Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Sticky cart */}
        <aside className="lg:col-span-1">
          <Card className="border-2 lg:sticky lg:top-4">
            <CardContent className="p-4 space-y-3">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <ShoppingBag className="h-4 w-4" /> Punchout Cart
              </h2>
              {cart.length === 0 ? (
                <p className="text-sm text-slate-500">Add items, then transfer them back to your procurement system.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-auto pr-1">
                  {cart.map(i => (
                    <div key={i.product_id} className="flex gap-2 items-center border-b border-slate-100 pb-2" data-testid={`punchout-cart-row-${i.product_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{i.name}</div>
                        <div className="text-xs text-slate-500">${i.price.toFixed(2)} ea</div>
                      </div>
                      <div className="flex items-center border rounded">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => updateQty(i.product_id, i.quantity - 1)}><Minus className="h-3 w-3" /></Button>
                        <span className="px-2 text-xs font-semibold w-6 text-center">{i.quantity}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => updateQty(i.product_id, i.quantity + 1)}><Plus className="h-3 w-3" /></Button>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-600 h-6 w-6 p-0" onClick={() => updateQty(i.product_id, 0)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between border-t pt-2 text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-bold text-blue-700" data-testid="punchout-total">${total.toFixed(2)}</span>
              </div>
              <Button
                onClick={transferCart}
                disabled={!cart.length || transferring}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="punchout-transfer-btn"
              >
                <Send className="h-4 w-4 mr-1" />
                {transferring ? 'Transferring…' : 'Transfer Cart to Buyer'}
              </Button>
              <p className="text-[10px] text-slate-400 text-center">
                <LogOut className="inline h-3 w-3 mr-0.5" />
                After transfer, your browser will return to your procurement system.
              </p>
            </CardContent>
          </Card>
        </aside>
      </main>

      {/* Hidden form auto-submitted after we receive the cXML POOM. */}
      {formAction && formCxmlValue && (
        <form ref={formRef} action={formAction} method="POST" target="_top" style={{ display: 'none' }} data-testid="punchout-return-form">
          <input type="hidden" name={formEncoding} value={formCxmlValue} />
        </form>
      )}
    </div>
  );
};

export default GovPunchoutCatalog;
